import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer, PersistedState } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import authReducer from './slices/authSlice';
import groupsReducer from './slices/groupsSlice';
import studentsReducer from './slices/studentsSlice';
import telegramReducer from './slices/telegramSlice';
import lessonsReducer from './slices/lessonsSlice';
import vocabularyReducer from './slices/vocabularySlice';
import certificatesReducer from './slices/certificatesSlice';
import coursesReducer from './slices/coursesSlice';
import analyticsReducer from './slices/analyticsSlice';
import dashboardReducer from './slices/dashboardSlice';
import adsReducer from './slices/adsSlice';
import consultationsReducer from './slices/consultationsSlice';
import recordingsReducer from './slices/recordingsSlice';
import vacationsReducer from './slices/vacationsSlice';
import contactsReducer from './slices/contactsSlice';
import journalReducer from './slices/journalSlice';
import accountingReducer from './slices/accountingSlice';
import statsReducer from './slices/statsSlice';

const persistConfig = {
  key: 'root',
  version: 1,
  storage,
  whitelist: ['auth'], // Only persist auth state
  migrate: (state: PersistedState) => {
    // Reset loading state to prevent stuck "signing in" state
    const persisted = state as
      | (PersistedState & { auth?: { loading?: boolean; sessionLoading?: boolean } });
    if (persisted && persisted.auth) {
      persisted.auth.loading = false;
      // Remove old sessionLoading if it exists
      delete persisted.auth.sessionLoading;
    }
    return Promise.resolve(persisted);
  }
};

export const rootReducer = combineReducers({
  auth: authReducer,
  groups: groupsReducer,
  students: studentsReducer,
  telegram: telegramReducer,
  lessons: lessonsReducer,
  vocabulary: vocabularyReducer,
  certificates: certificatesReducer,
  courses: coursesReducer,
  analytics: analyticsReducer,
  dashboard: dashboardReducer,
  ads: adsReducer,
  consultations: consultationsReducer,
  recordings: recordingsReducer,
  vacations: vacationsReducer,
  contacts: contactsReducer,
  journal: journalReducer,
  accounting: accountingReducer,
  stats: statsReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch; 